import React, { useEffect, useState } from 'react';
import { rocketCityEventsService } from '../../services/rocketCityEventsService';
import styles from './AdminLayout.module.css';

export default function AdminEvents() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [form, setForm] = useState({ 
    title: '', 
    start_date: '', 
    end_date: '',
    description: '', 
    venue: '',
    category: 'Other',
    event_type: 'General',
    organizer: '',
    cost: '',
    age_group: '',
    registration_required: false,
    registration_link: '',
    url: '',
    tags: ''
  });
  const [error, setError] = useState('');
  const [stats, setStats] = useState(null);
  const [aiTagsBusy, setAiTagsBusy] = useState(false);
  const [aiTagsError, setAiTagsError] = useState('');
  const [aiTagsInfo, setAiTagsInfo] = useState(null);

  const WORKER_URL = 'https://sync.rocketcitynerd.com';

  // Fetch Rocket City events
  const fetchEvents = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await rocketCityEventsService.getEvents();
      setEvents(data);
      
      // Get stats
      const statsData = await rocketCityEventsService.getEventStats();
      setStats(statsData);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  // Handle form change
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const generateAutoTags = async () => {
    setAiTagsError('');
    setAiTagsInfo(null);
    if (!form.title && !form.description) {
      setAiTagsError('Add a title or description first.');
      return;
    }
    try {
      setAiTagsBusy(true);
      const textParts = [
        `Title: ${form.title || ''}`,
        form.description ? `Description: ${form.description}` : '',
        form.category ? `Category: ${form.category}` : '',
        form.venue ? `Venue: ${form.venue}` : '',
        form.organizer ? `Organizer: ${form.organizer}` : '',
        form.cost ? `Cost: ${form.cost}` : '',
        form.age_group ? `Age group: ${form.age_group}` : '',
      ].filter(Boolean);
      const text = textParts.join('\n').slice(0, 8000);
      const res = await fetch(`${WORKER_URL}/api/ai/summarize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text,
          system: 'Extract 3-7 topical tags for this local event. Use lowercase, hyphen-separated (e.g., live-music, family-friendly). Avoid duplicates, emojis, and punctuation. Output ONLY a JSON array of strings.',
          maxTokens: 64,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || `Request failed: ${res.status}`);
      let raw = (data?.summary || '').toString().trim();
      // Try to parse JSON array; fallback to CSV parsing
      let tags = [];
      try {
        // If the model wrapped JSON in code fences, strip them
        const m = raw.match(/\[.*\]/s);
        if (m) raw = m[0];
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) tags = arr;
      } catch {
        tags = raw
          .replace(/[#\[\]\n`]/g, '')
          .split(/[,;]+/)
          .map((s) => s.trim())
          .filter(Boolean);
      }
      // Normalize tags
      const norm = Array.from(new Set(tags.map(String)))
        .map((t) => t.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''))
        .filter(Boolean)
        .slice(0, 10);
      if (norm.length === 0) {
        throw new Error('No tags returned');
      }
      setForm((prev) => ({ ...prev, tags: norm.join(', ') }));
      setAiTagsInfo({ provider: data?.provider, model: data?.model });
    } catch (err) {
      setAiTagsError(err instanceof Error ? err.message : String(err));
    } finally {
      setAiTagsBusy(false);
    }
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.start_date) {
      setError('Title and start date are required.');
      return;
    }

    try {
      const eventData = {
        ...form,
        tags: form.tags ? form.tags.split(',').map(tag => tag.trim()) : [],
        source_feed: 'Manual'
      };

      if (editingEvent) {
        await rocketCityEventsService.updateEvent(editingEvent.id, eventData);
      } else {
        await rocketCityEventsService.addEvent(eventData);
      }

      setForm({ 
        title: '', 
        start_date: '', 
        end_date: '',
        description: '', 
        venue: '',
        category: 'Other',
        event_type: 'General',
        organizer: '',
        cost: '',
        age_group: '',
        registration_required: false,
        registration_link: '',
        url: '',
        tags: ''
      });
      setEditingEvent(null);
      await fetchEvents();
    } catch (err) {
      setError(err.message);
    }
  };

  // Edit event
  const handleEdit = (event) => {
    setForm({
      title: event.title,
      start_date: event.start_date ? event.start_date.slice(0, 16) : '',
      end_date: event.end_date ? event.end_date.slice(0, 16) : '',
      description: event.description || '',
      venue: event.venue || '',
      category: event.category || 'Other',
      event_type: event.event_type || 'General',
      organizer: event.organizer || '',
      cost: event.cost || '',
      age_group: event.age_group || '',
      registration_required: event.registration_required || false,
      registration_link: event.registration_link || '',
      url: event.url || '',
      tags: event.tags ? event.tags.join(', ') : ''
    });
    setEditingEvent(event);
  };

  // Handle cancel edit
  const handleCancel = () => {
    setForm({ 
      title: '', 
      start_date: '', 
      end_date: '',
      description: '', 
      venue: '',
      category: 'Other',
      event_type: 'General',
      organizer: '',
      cost: '',
      age_group: '',
      registration_required: false,
      registration_link: '',
      url: '',
      tags: ''
    });
    setEditingEvent(null);
  };

  // Delete event
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    
    setLoading(true);
    setError('');
    try {
      await rocketCityEventsService.deleteEvent(id);
      await fetchEvents();
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  // Download ICS calendar
  const handleDownloadICS = async () => {
    try {
      const icsData = await rocketCityEventsService.generateICS();
      const blob = new Blob([icsData], { type: 'text/calendar' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'rocket-city-events.ics';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to generate calendar file: ' + err.message);
    }
  };

  return (
    <div
      className="admin-events-container"
      style={{ maxWidth: 900, margin: 'auto', padding: 24 }}
    >
      <h2>Rocket City Events</h2>
      
      {stats && (
        <div style={{ marginBottom: 24, padding: 16, backgroundColor: '#f5f5f5', borderRadius: 8 }}>
          <h4>Event Statistics</h4>
          <p>Total Events: {stats.total} | Upcoming: {stats.upcoming}</p>
          <button 
            onClick={handleDownloadICS}
            style={{ 
              padding: '6px 12px', 
              backgroundColor: '#28a745', 
              color: 'white', 
              border: 'none', 
              borderRadius: 4, 
              fontSize: '14px',
              marginTop: 8
            }}
          >
            📅 Download Calendar (ICS)
          </button>
        </div>
      )}
      
      {error && <div style={{ color: 'red', marginBottom: 16, padding: 12, backgroundColor: '#f8d7da', borderRadius: 4 }}>{error}</div>}
      
      <form onSubmit={handleSubmit} style={{ marginBottom: 24, display: 'grid', gap: 16 }}>
        <h3>{editingEvent ? 'Edit Event' : 'Add New Event'}</h3>
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div>
            <label>
              Title *:
              <input
                name="title"
                value={form.title}
                onChange={handleChange}
                required
                style={{ width: '100%', padding: 8, marginTop: 4 }}
              />
            </label>
          </div>
          <div>
            <label>
              Category:
              <select
                name="category"
                value={form.category}
                onChange={handleChange}
                style={{ width: '100%', padding: 8, marginTop: 4 }}
              >
                <option value="Space & Science">Space & Science</option>
                <option value="Planetarium Show">Planetarium Show</option>
                <option value="Space Camp">Space Camp</option>
                <option value="Concert">Concert</option>
                <option value="Sports">Sports</option>
                <option value="Museum">Museum</option>
                <option value="Maker Meetup">Maker Meetup</option>
                <option value="Conference">Conference</option>
                <option value="Festival">Festival</option>
                <option value="Education">Education</option>
                <option value="Family">Family</option>
                <option value="Other">Other</option>
              </select>
            </label>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div>
            <label>
              Start Date/Time *:
              <input
                name="start_date"
                type="datetime-local"
                value={form.start_date}
                onChange={handleChange}
                required
                style={{ width: '100%', padding: 8, marginTop: 4 }}
              />
            </label>
          </div>
          <div>
            <label>
              End Date/Time:
              <input
                name="end_date"
                type="datetime-local"
                value={form.end_date}
                onChange={handleChange}
                style={{ width: '100%', padding: 8, marginTop: 4 }}
              />
            </label>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div>
            <label>
              Venue:
              <input
                name="venue"
                value={form.venue}
                onChange={handleChange}
                placeholder="e.g., Von Braun Center, USSRC"
                style={{ width: '100%', padding: 8, marginTop: 4 }}
              />
            </label>
          </div>
          <div>
            <label>
              Organizer:
              <input
                name="organizer"
                value={form.organizer}
                onChange={handleChange}
                style={{ width: '100%', padding: 8, marginTop: 4 }}
              />
            </label>
          </div>
        </div>

        <div>
          <label>
            Description:
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              rows={3}
              style={{ width: '100%', padding: 8, marginTop: 4 }}
            />
          </label>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
          <div>
            <label>
              Cost:
              <input
                name="cost"
                value={form.cost}
                onChange={handleChange}
                placeholder="Free, $20, $10-25"
                style={{ width: '100%', padding: 8, marginTop: 4 }}
              />
            </label>
          </div>
          <div>
            <label>
              Age Group:
              <input
                name="age_group"
                value={form.age_group}
                onChange={handleChange}
                placeholder="All Ages, K-5, Adult"
                style={{ width: '100%', padding: 8, marginTop: 4 }}
              />
            </label>
          </div>
          <div>
            <label>
              Event Type:
              <select
                name="event_type"
                value={form.event_type}
                onChange={handleChange}
                style={{ width: '100%', padding: 8, marginTop: 4 }}
              >
                <option value="General">General</option>
                <option value="STEM">STEM</option>
                <option value="Entertainment">Entertainment</option>
                <option value="Sports">Sports</option>
                <option value="Education">Education</option>
                <option value="Community">Community</option>
              </select>
            </label>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div>
            <label>
              Website URL:
              <input
                name="url"
                type="url"
                value={form.url}
                onChange={handleChange}
                style={{ width: '100%', padding: 8, marginTop: 4 }}
              />
            </label>
          </div>
          <div>
            <label>
              Registration Link:
              <input
                name="registration_link"
                type="url"
                value={form.registration_link}
                onChange={handleChange}
                style={{ width: '100%', padding: 8, marginTop: 4 }}
              />
            </label>
          </div>
        </div>

        <div>
          <label>
            Tags (comma-separated):
            <input
              name="tags"
              value={form.tags}
              onChange={handleChange}
              placeholder="space, family-friendly, outdoor"
              style={{ width: '100%', padding: 8, marginTop: 4 }}
            />
          </label>
          <div className={styles.inlineRow} style={{ marginTop: 8 }}>
            <button
              type="button"
              onClick={generateAutoTags}
              disabled={aiTagsBusy || (!form.title && !form.description)}
              className={`${styles.button} ${styles.buttonSecondary}`}
              title={!form.title && !form.description ? 'Enter a title or description first' : 'Use AI to suggest tags'}
            >
              {aiTagsBusy ? 'Tagging…' : 'Auto-tag (AI)'}
            </button>
            {aiTagsError && <span className={styles.errorDetails}>AI: {aiTagsError}</span>}
            {!aiTagsError && aiTagsInfo && (
              <span className={styles.muted}>
                AI {aiTagsInfo.provider || 'unknown'}{aiTagsInfo.model ? `/${aiTagsInfo.model}` : ''}
              </span>
            )}
          </div>
        </div>

        <div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              name="registration_required"
              type="checkbox"
              checked={form.registration_required}
              onChange={handleChange}
            />
            Registration Required
          </label>
        </div>

        <div style={{ display: 'flex', gap: 12 }}>
          <button 
            type="submit" 
            disabled={loading}
            style={{ 
              padding: '8px 16px', 
              backgroundColor: '#007bff', 
              color: 'white', 
              border: 'none', 
              borderRadius: 4,
              opacity: loading ? 0.6 : 1 
            }}
          >
            {loading ? 'Saving...' : (editingEvent ? 'Update Event' : 'Add Event')}
          </button>
          {editingEvent && (
            <button 
              type="button" 
              onClick={handleCancel} 
              style={{ 
                padding: '8px 16px', 
                backgroundColor: '#6c757d', 
                color: 'white', 
                border: 'none', 
                borderRadius: 4 
              }}
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      <hr style={{ margin: '24px 0' }} />
      <h3>Event List</h3>
      
      {loading ? (
        <p>Loading...</p>
      ) : events.length === 0 ? (
        <p>No events found. Add your first Rocket City event above!</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className={styles.table} style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                <th style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Title</th>
                <th style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Date</th>
                <th style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Venue</th>
                <th style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Category</th>
                <th style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Cost</th>
                <th style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {events.map((event) => (
                <tr key={event.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                  <td style={{ padding: 12 }}>
                    <strong>{event.title}</strong>
                    {event.description && (
                      <div style={{ fontSize: '14px', color: '#666', marginTop: 4 }}>
                        {event.description.substring(0, 100)}
                        {event.description.length > 100 ? '...' : ''}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: 12, fontSize: '14px' }}>
                    {new Date(event.start_date).toLocaleDateString()} 
                    <br />
                    {new Date(event.start_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </td>
                  <td style={{ padding: 12, fontSize: '14px' }}>{event.venue || '-'}</td>
                  <td style={{ padding: 12, fontSize: '14px' }}>
                    <span style={{ 
                      padding: '2px 8px', 
                      backgroundColor: '#e9ecef', 
                      borderRadius: 12, 
                      fontSize: '12px' 
                    }}>
                      {event.category}
                    </span>
                  </td>
                  <td style={{ padding: 12, fontSize: '14px' }}>{event.cost || 'Free'}</td>
                  <td style={{ padding: 12 }}>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        onClick={() => handleEdit(event)}
                        style={{ 
                          padding: '4px 8px', 
                          backgroundColor: '#ffc107', 
                          color: 'black', 
                          border: 'none', 
                          borderRadius: 4, 
                          fontSize: '12px' 
                        }}
                      >
                        Edit
                      </button>
                      <button 
                        onClick={() => handleDelete(event.id)}
                        style={{ 
                          padding: '4px 8px', 
                          backgroundColor: '#dc3545', 
                          color: 'white', 
                          border: 'none', 
                          borderRadius: 4, 
                          fontSize: '12px' 
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
